import React from "react";
import { DropdownInput, IOption, Checkbox } from "component-library";

import {
  withEditBasicInfoContext,
  IEditBasicInfoContextProps,
} from "../../../../../../contexts/EditBasicInfoContext";
import { ControlSection } from "../../../../../Common/ControlSection";
import { IDancingViewModel } from "../../../../models/views";
import { MeasurementSystems } from "../../../../models";

import Weight, { IWeightContent } from "./Weight";

export interface IDancingContent extends IWeightContent {
  dancingSectionName: string;
  dancingDescription: string;
  isVisibleDancerMeasurementsLabel: string;
  chestSizeField: string;
  chestSizePlaceholder: string;
  waistSizeField: string;
  waistSizePlaceholder: string;
  hipsSizeField: string;
  hipsSizePlaceholder: string;
  insideLegSizeField: string;
  insideLegSizePlaceholder: string;
  insideArmSizeField: string;
  insideArmSizePlaceholder: string;
  collarSizeField: string;
  collarSizePlaceholder: string;
  hatSizeField: string;
  hatSizePlaceholder: string;
  shoeSizeField: string;
  shoeSizePlaceholder: string;
  dressSizeField: string;
  dressSizePlaceholder: string;
  imperialSystemName: string;
  metricSystemName: string;
  weightSectionName: string;
}

interface IDancingProps extends IEditBasicInfoContextProps {
  model: IDancingViewModel;
  content: IDancingContent;
  handleChanges: (model: IDancingViewModel) => void;
}

interface IDancingState {
  measurementSystem: MeasurementSystems;
}

export class Dancing extends React.Component<IDancingProps, IDancingState> {
  constructor(props: IDancingProps) {
    super(props);

    this.state = {
      measurementSystem: MeasurementSystems.imperial,
    };
  }

  public render() {
    const { measurementSystem } = this.state;
    const {
      model,
      content,
      sharedState: { isDancerMeasurementsVisible },
    } = this.props;

    return (
      <React.Fragment>
        <ControlSection
          name={content.dancingSectionName}
          description={content.dancingDescription}
        >
          <div className="c-additional-info-form__show-dancer-measurements">
            <Checkbox
              htmlId="dancingCheckbox"
              label={content.isVisibleDancerMeasurementsLabel}
              ariaLabel={content.isVisibleDancerMeasurementsLabel}
              checked={isDancerMeasurementsVisible}
              value=""
              name="is-visible-dancer-measurements-label"
              valueChanged={this.toggleDancerMeasurements}
            />
          </div>
        </ControlSection>
        {isDancerMeasurementsVisible && (
          <ControlSection
            button={{
              name:
                measurementSystem === MeasurementSystems.imperial
                  ? content.metricSystemName
                  : content.imperialSystemName,
              clickHandler: this.changeMeasurementSystem,
            }}
          >
            <div className="c-additional-info-form__control-grid-2">
              <div className="c-additional-info-form__control-grid-cell">
                {this.renderConvertibleDropDown("chestSize", {
                  label: content.chestSizeField,
                  placeholder: content.chestSizePlaceholder,
                })}
              </div>
              <div className="c-additional-info-form__control-grid-cell">
                {this.renderConvertibleDropDown("waistSize", {
                  label: content.waistSizeField,
                  placeholder: content.waistSizePlaceholder,
                })}
              </div>
              <div className="c-additional-info-form__control-grid-cell">
                {this.renderConvertibleDropDown("hipsSize", {
                  label: content.hipsSizeField,
                  placeholder: content.hipsSizePlaceholder,
                })}
              </div>
              <div className="c-additional-info-form__control-grid-cell" />
              <div className="c-additional-info-form__control-grid-cell">
                {this.renderConvertibleDropDown("insideLegSize", {
                  label: content.insideLegSizeField,
                  placeholder: content.insideLegSizePlaceholder,
                })}
              </div>
              <div className="c-additional-info-form__control-grid-cell">
                {this.renderConvertibleDropDown("insideArmSize", {
                  label: content.insideArmSizeField,
                  placeholder: content.insideArmSizePlaceholder,
                })}
              </div>
              <div className="c-additional-info-form__control-grid-cell">
                {this.renderConvertibleDropDown("collarSize", {
                  label: content.collarSizeField,
                  placeholder: content.collarSizePlaceholder,
                })}
              </div>
              <div className="c-additional-info-form__control-grid-cell">
                {this.renderConvertibleDropDown("hatSize", {
                  label: content.hatSizeField,
                  placeholder: content.hatSizePlaceholder,
                })}
              </div>
              <div className="c-additional-info-form__control-grid-cell">
                {this.renderDropDown("shoeSize", {
                  label: content.shoeSizeField,
                  placeholder: content.shoeSizePlaceholder,
                })}
              </div>
              <div className="c-additional-info-form__control-grid-cell">
                {this.renderDropDown("dressSize", {
                  label: content.dressSizeField,
                  placeholder: content.dressSizePlaceholder,
                })}
              </div>
            </div>
            <div className="c-additional-info-form__subsection">
              {content.weightSectionName}
            </div>
            <Weight
              model={model}
              measurementSystem={measurementSystem}
              content={content}
              handleChanges={this.props.handleChanges}
            />
          </ControlSection>
        )}
      </React.Fragment>
    );
  }

  private renderConvertibleDropDown = (
    name: string,
    text: { label: string; placeholder: string }
  ) => {
    const isMetric = this.state.measurementSystem === MeasurementSystems.metric;
    const modelValue = this.props.model[name];
    const options = [
      this.getEmptyOption(text.placeholder),
      ...this.getOptionsWithMeasurements(name, isMetric),
    ];

    const dropdownValue = Math.round(
      isMetric ? modelValue : this.centimetersToInches(modelValue)
    );

    return (
      <DropdownInput<number>
        key={name + this.state.measurementSystem.toString()}
        id={`dancingConvertible${name}`}
        variant="select"
        options={options}
        name={name}
        value={dropdownValue}
        {...text}
        valueChanged={this.convertibleDropdownChanged(name, isMetric)}
      />
    );
  };

  private convertibleDropdownChanged = (
    fieldName: string,
    isMetric: boolean
  ) => {
    return (value: number) => {
      const updatedModel = { ...this.props.model };

      let convertedValue = isMetric ? value : this.inchesToCentimeters(value);
      updatedModel[fieldName] = Math.round(convertedValue * 10) / 10;

      if (updatedModel[fieldName] !== this.props.model[fieldName]) {
        this.props.handleChanges(updatedModel);
      }
    };
  };

  private renderDropDown = (
    name: string,
    text: { label: string; placeholder: string }
  ) => {
    const modelValue = this.props.model[name];
    const options = [
      this.getEmptyOption(text.placeholder),
      ...this.getOptionsWithoutMeasurements(name),
    ];

    return (
      <DropdownInput<number>
        key={name + this.state.measurementSystem.toString()}
        id={`dancing${name}`}
        variant="select"
        options={options}
        name={name}
        value={modelValue}
        {...text}
        valueChanged={this.dropdownChanged(name)}
      />
    );
  };

  private dropdownChanged = (fieldName: string) => {
    return (value: number) => {
      const updatedModel = { ...this.props.model };

      updatedModel[fieldName] = value;

      if (updatedModel[fieldName] !== this.props.model[fieldName]) {
        this.props.handleChanges(updatedModel);
      }
    };
  };

  private toggleDancerMeasurements = (value: boolean) =>
    this.props.sharedState.changeSharedState({
      isDancerMeasurementsVisible: value,
    });

  private changeMeasurementSystem = () =>
    this.setState((state) => ({
      measurementSystem:
        state.measurementSystem === MeasurementSystems.imperial
          ? MeasurementSystems.metric
          : MeasurementSystems.imperial,
    }));

  private getOptionsWithMeasurements = (name: string, isMetric: boolean) => {
    const measurementSystemKey = isMetric ? "metric" : "imperial";
    const { minValue, maxValue, step } =
      CONVERTIBLE_MEASUREMENTS_VALUES_LIMITS[name][measurementSystemKey];

    const options = this.getNumberList(minValue, maxValue, step);

    return options.map((item) => ({
      name: isMetric ? item.name + " cm" : item.name + " inches",
      value: item.value,
    }));
  };

  private getEmptyOption = (placeholder: string): IOption<number> => ({
    value: NaN,
    name: placeholder,
  });

  private getOptionsWithoutMeasurements = (name: string) => {
    const { minValue, maxValue, step } =
      NOT_CONVERTIBLE_MEASUREMENTS_VALUES_LIMITS[name];

    return this.getNumberList(minValue, maxValue, step);
  };

  private getNumberList = (from: number, to: number, step: number = 1) => {
    const options: IOption<number>[] = [];
    for (let index = from; index <= to; index += step) {
      options.push({ name: index.toString(), value: index });
    }

    return options;
  };

  private centimetersToInches = (centimeters: number) => centimeters * 0.3937;

  private inchesToCentimeters = (inches: number) => inches / 0.3937;
}

export default withEditBasicInfoContext(Dancing);

export const CONVERTIBLE_MEASUREMENTS_VALUES_LIMITS = {
  chestSize: {
    metric: { minValue: 48, maxValue: 150 },
    imperial: { minValue: 19, maxValue: 59 },
  },
  waistSize: {
    metric: { minValue: 51, maxValue: 150 },
    imperial: { minValue: 20, maxValue: 59 },
  },
  hipsSize: {
    metric: { minValue: 53, maxValue: 150 },
    imperial: { minValue: 21, maxValue: 59 },
  },
  insideLegSize: {
    metric: { minValue: 46, maxValue: 112 },
    imperial: { minValue: 18, maxValue: 44 },
  },
  insideArmSize: {
    metric: { minValue: 28, maxValue: 152 },
    imperial: { minValue: 11, maxValue: 60 },
  },
  collarSize: {
    metric: { minValue: 25, maxValue: 61 },
    imperial: { minValue: 10, maxValue: 24 },
  },
  hatSize: {
    metric: { minValue: 28, maxValue: 67 },
    imperial: { minValue: 11, maxValue: 26 },
  },
};

export const NOT_CONVERTIBLE_MEASUREMENTS_VALUES_LIMITS = {
  shoeSize: { minValue: 2, maxValue: 16, step: 1 },
  dressSize: { minValue: 0, maxValue: 24, step: 2 },
};
